import React from "react";
import { Select } from "antd";
import { useSelector } from "react-redux";

import * as api from "../Api/index";
import { BANK_BRANCHES } from "../constants/bank";

// User records store branches lowercase (the server lowercases them too)
const normalize = (value) => (value || "").trim().toLowerCase();

// Branches from the server: company locations plus the branches existing users
// are in. Falls back to the list in constants/bank.js only if the server has
// no locations (or can't be reached).
export function useBranches() {
  const users = useSelector((state) => state.users || []);
  const [locations, setLocations] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;

    api
      .fetchLocations()
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data)) {
          setLocations(data.map((location) => location?.name));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  return React.useMemo(() => {
    const names = new Set(["head office"]);
    const add = (value) => normalize(value) && names.add(normalize(value));

    locations.forEach(add);
    users.forEach((user) => add(user.branch));
    if (locations.length === 0) BANK_BRANCHES.forEach(add);

    return [...names].sort((a, b) =>
      a === "head office" ? -1 : b === "head office" ? 1 : a.localeCompare(b),
    );
  }, [locations, users]);
}

// Searchable branch picker; typing a name that isn't listed offers to add it.
export default function BranchSelect({ value, onChange, ...props }) {
  const branches = useBranches();
  const [search, setSearch] = React.useState("");
  const typed = normalize(search);

  const options = branches.map((branch) => ({ label: branch, value: branch }));
  if (typed && !branches.includes(typed)) {
    options.push({ label: `Add new branch "${typed}"`, value: typed });
  }

  return (
    <Select
      showSearch
      placeholder="Select branch"
      optionFilterProp="label"
      {...props}
      value={value}
      options={options}
      onSearch={setSearch}
      onChange={(next) => {
        setSearch("");
        onChange?.(next);
      }}
    />
  );
}
